import { base_URL } from "../config/config";
import { isNetworkIdle } from "./requestTracker";

/**
 * Impression queue — analytics calls must never block list/data API calls.
 *
 * Design:
 *  • Module-level singleton: all cards share one queue (not one per component).
 *  • Session-level dedup: a venueId is sent at most once per page session.
 *  • Network-idle gating: each impression waits until no axios API calls are
 *    in-flight before it fires, so it never competes with data fetches for
 *    the browser's 6 connection slots per domain.
 *  • Sequential processing with a 300 ms gap between sends.
 *  • keepalive:true so the request completes even if the user navigates away.
 */

const seen = new Set();   // venueIds already sent this session
const queue = [];         // pending impression payloads
let processing = false;

export function queueImpression(data) {
  if (!data?.venueId || seen.has(data.venueId)) return;
  seen.add(data.venueId);   // mark immediately — prevents race-condition dupes
  queue.push(data);
  scheduleFlush();
}

function scheduleFlush() {
  if (processing) return;
  // Small initial delay so any synchronous request that just fired has time
  // to register in the tracker before we start checking.
  setTimeout(flush, 300);
}

/**
 * Wait until no axios API calls are in-flight, or until maxWaitMs elapses
 * (so impressions don't get stuck forever if a request hangs).
 */
function waitForNetworkIdle(maxWaitMs = 10_000) {
  const deadline = Date.now() + maxWaitMs;
  return new Promise((resolve) => {
    const check = () => {
      if (isNetworkIdle() || Date.now() >= deadline) {
        resolve();
      } else {
        setTimeout(check, 150);
      }
    };
    check();
  });
}

async function flush() {
  if (processing || queue.length === 0) return;
  processing = true;

  while (queue.length > 0) {
    await waitForNetworkIdle();   // hold until no data API calls are active
    const item = queue.shift();
    await sendOne(item);
    if (queue.length > 0) {
      // 300 ms breathing room between sends — never saturates the connection pool
      await delay(300);
    }
  }

  processing = false;
}

async function sendOne(data) {
  try {
    await fetch(`${base_URL}/api/impressions/update-impressions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      keepalive: true,   // completes even if user navigates away mid-send
    });
  } catch {
    // analytics failure is non-critical — silently ignore
  }
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
